// mapDevViewer.ts — BETA 23.00 · P0 of the Map Rendering Upgrade Plan
//
// DEV MAP VIEWER (fix pack v4 doc 09 + v6 docs 12/13 debug overlays + v7
// doc 14 depth/parallax test). Inspect the map WITHOUT playing:
//
//   Enable:  load the game with  ?mapdev=1   (works in the muted preview)
//   While enabled, gameplay is FROZEN (no update tick) and:
//     WASD / arrows ... pan the free camera (shift = 3× speed)
//     - / = ........... zoom out / in (0.4×–1.6×)
//     r ............... recenter on the player
//     b ............... chunk boundaries (debug only — never in gameplay)
//     o ............... collision/POI overlays: machine footprint colliders,
//                       interaction radii, shrine capture + reserved radii,
//                       player foot collider + feet point, y-sort base dots
//     h ............... HORIZON PREVIEW (doc 14 depth test): sky band +
//                       parallax silhouettes + distance fade, drawn exactly
//                       as P4 will ship them — pan sideways to verify the
//                       skyline moves slower than the ground
//     1..6 ............ toggle ground layers (1 brightness, 2 speckles,
//                       3 art patches, 4 road paint, 5 decals, 6 base fill)
//
// This module owns dev state only; engine.ts calls into it at three points:
// updateGame gate, renderGame overlay pass, and mapChunks layer checks.

export const MAPDEV = {
  on: false,
  cam: { x: 0, y: 0, zoom: 1 },
  initialized: false,
  showChunkBounds: false,
  showOverlays: true,          // colliders/radii default ON in dev
  showHorizon: false,          // 23.6x Dylan: "remove this city looking shadows at the top" — default OFF ([h] still toggles the doc-14 depth test on when needed)
  // ground layer toggles (mapChunks checks these; toggling clears the cache)
  layers: {
    base: true, brightness: true, speckles: true,
    patches: true, paint: true, decals: true,
  },
  // BETA 23.06 - DT-A additions
  showPerf: true,          // [f] perf overlay (fps / counts)
  showEntities: true,      // [7] enemies + dummies
  showUI: true,            // [8] HUD
  showProps: true,         // [9] machines/shrines/props
  poiIndex: 0,             // [n]/[m] jump cursor
  wantScreenshot: false,   // [p] one-shot flag, consumed in the render pass
  showForestDebug: false,  // [t] 23.45 — tree base dots + band colors + walkable-mask tint
  showCorruption: false,   // [c] 23.54 — corruption influence heatmap + crack cluster bounds/families
};

export function mapDevRequested(): boolean {
  try {
    return typeof window !== 'undefined'
      && new URLSearchParams(window.location.search).has('mapdev');
  } catch { return false; }
}

/** One-shot key edge detection (keys Set is held-state). */
const _prevKeys = new Set<string>();
function _pressed(keys: Set<string>, k: string): boolean {
  const now = keys.has(k);
  const was = _prevKeys.has(k);
  if (now) _prevKeys.add(k); else _prevKeys.delete(k);
  return now && !was;
}

export interface MapDevHooks {
  playerX: number;
  playerY: number;
  clearChunkCache: () => void;
  // BETA 23.06 - POI jump targets ([n] machines, [m] shrines)
  machines?: Array<{ x: number; y: number }>;
  shrines?: Array<{ x: number; y: number }>;
}

/**
 * Called from updateGame while MAPDEV.on — handles the free camera + toggles
 * and returns true (meaning: skip the gameplay update entirely).
 */
export function mapDevTick(keys: Set<string>, dt: number, hooks: MapDevHooks): boolean {
  const c = MAPDEV.cam;
  if (!MAPDEV.initialized) {
    c.x = hooks.playerX; c.y = hooks.playerY; c.zoom = 1;
    MAPDEV.initialized = true;
  }

  const fast = keys.has('shift') ? 3 : 1;
  const sp = (900 / c.zoom) * fast * dt;
  if (keys.has('w') || keys.has('arrowup')) c.y -= sp;
  if (keys.has('s') || keys.has('arrowdown')) c.y += sp;
  if (keys.has('a') || keys.has('arrowleft')) c.x -= sp;
  if (keys.has('d') || keys.has('arrowright')) c.x += sp;
  if (keys.has('-')) c.zoom = Math.max(0.05, c.zoom - Math.max(0.35, c.zoom) * 1.6 * dt);   // 23.43: Dylan — "zoom out as far as i want"
  if (keys.has('=') || keys.has('+')) c.zoom = Math.min(1.6, c.zoom + 1.2 * dt);

  if (_pressed(keys, 'r')) { c.x = hooks.playerX; c.y = hooks.playerY; c.zoom = 1; }
  if (_pressed(keys, 'b')) MAPDEV.showChunkBounds = !MAPDEV.showChunkBounds;
  if (_pressed(keys, 'o')) MAPDEV.showOverlays = !MAPDEV.showOverlays;
  if (_pressed(keys, 'h')) MAPDEV.showHorizon = !MAPDEV.showHorizon;

  // BETA 23.06 - DT-A keys
  if (_pressed(keys, 't')) MAPDEV.showForestDebug = !MAPDEV.showForestDebug;   // 23.45
  if (_pressed(keys, 'c')) MAPDEV.showCorruption = !MAPDEV.showCorruption;     // 23.54
  if (_pressed(keys, 'f')) MAPDEV.showPerf = !MAPDEV.showPerf;
  if (_pressed(keys, '7')) MAPDEV.showEntities = !MAPDEV.showEntities;
  if (_pressed(keys, '8')) MAPDEV.showUI = !MAPDEV.showUI;
  if (_pressed(keys, '9')) MAPDEV.showProps = !MAPDEV.showProps;
  if (_pressed(keys, 'p')) MAPDEV.wantScreenshot = true;
  if (_pressed(keys, 'n') && hooks.machines && hooks.machines.length) {
    const t = hooks.machines[MAPDEV.poiIndex % hooks.machines.length];
    MAPDEV.poiIndex++;
    c.x = t.x; c.y = t.y;
  }
  if (_pressed(keys, 'm') && hooks.shrines && hooks.shrines.length) {
    const t = hooks.shrines[MAPDEV.poiIndex % hooks.shrines.length];
    MAPDEV.poiIndex++;
    c.x = t.x; c.y = t.y;
  }

  const L = MAPDEV.layers;
  let dirty = false;
  if (_pressed(keys, '1')) { L.brightness = !L.brightness; dirty = true; }
  if (_pressed(keys, '2')) { L.speckles = !L.speckles; dirty = true; }
  if (_pressed(keys, '3')) { L.patches = !L.patches; dirty = true; }
  if (_pressed(keys, '4')) { L.paint = !L.paint; dirty = true; }
  if (_pressed(keys, '5')) { L.decals = !L.decals; dirty = true; }
  if (_pressed(keys, '6')) { L.base = !L.base; dirty = true; }
  if (dirty) hooks.clearChunkCache();

  return true;
}

// ─── HORIZON / DEPTH PREVIEW (v7 doc 14 — drawn as P4 will ship it) ────────
// Band D: sky/fog, parallax ~0.02 · Band C: silhouettes, parallax 0.10 ·
// distance fade over the top of the ground. Screen-space, after the world,
// before the UI. Deliberately subtle (doc: "visible but subtle").

export function drawHorizonPreview(
  ctx: CanvasRenderingContext2D, w: number, h: number, camX: number,
): void {
  ctx.save();

  // Band D — sky/fog band, top 16% (parallax ≈ 0: fixed on screen)
  const skyH = h * 0.16;
  const sg = ctx.createLinearGradient(0, 0, 0, skyH);
  sg.addColorStop(0, 'rgba(8, 10, 16, 0.92)');
  sg.addColorStop(0.55, 'rgba(14, 15, 22, 0.55)');
  sg.addColorStop(1, 'rgba(14, 15, 22, 0)');
  ctx.fillStyle = sg;
  ctx.fillRect(0, 0, w, skyH);

  // Band C — far ruined skyline, parallax 0.10, wrapping strip
  const par = 0.10;
  const strip = _getSkylineStrip();
  const sw = strip.width;
  let sx = (-camX * par) % sw;
  if (sx > 0) sx -= sw;
  ctx.globalAlpha = 0.38;
  const sy = h * 0.045;
  for (let x = sx; x < w; x += sw) {
    ctx.drawImage(strip, x, sy, sw, h * 0.13);
  }
  ctx.globalAlpha = 1;

  // distance fade — ground loses contrast toward the top (multiply, top 40%)
  const fadeH = h * 0.40;
  const fg = ctx.createLinearGradient(0, 0, 0, fadeH);
  fg.addColorStop(0, 'rgba(10, 11, 16, 0.40)');
  fg.addColorStop(1, 'rgba(10, 11, 16, 0)');
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = fg;
  ctx.fillRect(0, 0, w, fadeH);

  ctx.restore();
}

// code-drawn ruined skyline silhouette strip (v1 placeholder shapes; the P4
// ship version swaps in a sprite pack per doc 10's "advanced" section)
let _skyline: HTMLCanvasElement | null = null;
function _getSkylineStrip(): HTMLCanvasElement {
  if (_skyline) return _skyline;
  const W = 900, H = 120;
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = H;
  const c = cv.getContext('2d')!;
  c.fillStyle = 'rgba(4, 5, 8, 0.9)';
  // deterministic ruined blocks + poles + wires
  let s = 1337;
  const rng = () => { s = (Math.imul(s, 1664525) + 1013904223) >>> 0; return s / 0xFFFFFFFF; };
  let x = 0;
  while (x < W - 40) {
    const bw = 40 + rng() * 90;
    const bh = 25 + rng() * 70;
    c.fillRect(x, H - bh, bw, bh);
    // broken top notches
    if (rng() < 0.6) c.clearRect(x + bw * rng() * 0.6, H - bh, 8 + rng() * 14, 6 + rng() * 10);
    // window dots (very few, dim)
    if (rng() < 0.35) {
      c.fillStyle = 'rgba(160,140,90,0.25)';
      c.fillRect(x + 6 + rng() * (bw - 14), H - bh + 6 + rng() * (bh - 14), 3, 3);
      c.fillStyle = 'rgba(4, 5, 8, 0.9)';
    }
    x += bw + 14 + rng() * 55;
    // pole + wire between some gaps
    if (rng() < 0.5 && x < W - 30) {
      c.fillRect(x, H - 58, 3, 58);
      c.strokeStyle = 'rgba(4,5,8,0.75)';
      c.lineWidth = 1;
      c.beginPath();
      c.moveTo(x + 1, H - 52);
      c.quadraticCurveTo(x + 40, H - 38, x + 84, H - 50);
      c.stroke();
      x += 20;
    }
  }
  _skyline = cv;
  return cv;
}

// ─── DEBUG OVERLAYS (v6 docs 12/13 §7) ──────────────────────────────────────

export interface OverlayWorld {
  toScreenX: (wx: number) => number;
  toScreenY: (wy: number) => number;
  scale: number;
  chunkWu: number;
  machines: Array<{ x: number; y: number; gone: boolean; elite: boolean }>;
  shrines: Array<{ x: number; y: number }>;
  player: { x: number; y: number; footOff: number; size: number };
  interactRadius: number;
  viewLeft: number; viewTop: number; viewRight: number; viewBottom: number;
}

export function drawMapDevOverlays(
  ctx: CanvasRenderingContext2D, w: number, h: number, world: OverlayWorld,
): void {
  const { toScreenX: SX, toScreenY: SY, scale } = world;
  ctx.save();
  ctx.lineWidth = 1.5;

  if (MAPDEV.showChunkBounds) {
    ctx.strokeStyle = 'rgba(0,255,120,0.35)';
    const c0x = Math.floor(world.viewLeft / world.chunkWu), c1x = Math.floor(world.viewRight / world.chunkWu);
    const c0y = Math.floor(world.viewTop / world.chunkWu), c1y = Math.floor(world.viewBottom / world.chunkWu);
    for (let cx = c0x; cx <= c1x + 1; cx++) {
      ctx.beginPath(); ctx.moveTo(SX(cx * world.chunkWu), 0); ctx.lineTo(SX(cx * world.chunkWu), h); ctx.stroke();
    }
    for (let cy = c0y; cy <= c1y + 1; cy++) {
      ctx.beginPath(); ctx.moveTo(0, SY(cy * world.chunkWu)); ctx.lineTo(w, SY(cy * world.chunkWu)); ctx.stroke();
    }
  }

  if (MAPDEV.showOverlays) {
    for (const m of world.machines) {
      if (m.gone || m.elite) continue;
      // base footprint collider (red rect: ±52 × +8..+46 around chest center)
      ctx.strokeStyle = 'rgba(255,60,60,0.9)';
      ctx.strokeRect(SX(m.x - 52), SY(m.y + 8), 104 * scale, 38 * scale);
      // interaction radius (cyan)
      ctx.strokeStyle = 'rgba(0,220,255,0.45)';
      ctx.beginPath(); ctx.arc(SX(m.x), SY(m.y), world.interactRadius * scale, 0, Math.PI * 2); ctx.stroke();
      // y-sort base point (yellow dot at base line)
      ctx.fillStyle = '#ffe14d';
      ctx.beginPath(); ctx.arc(SX(m.x), SY(m.y + 40), 3, 0, Math.PI * 2); ctx.fill();
      // reserved POI radius (dashed magenta, doc-12 280wu)
      ctx.strokeStyle = 'rgba(255,60,214,0.35)';
      ctx.setLineDash([6, 6]);
      ctx.beginPath(); ctx.arc(SX(m.x), SY(m.y), 280 * scale, 0, Math.PI * 2); ctx.stroke();
      ctx.setLineDash([]);
    }
    for (const s of world.shrines) {
      // capture radius (blue) + reserved radius (dashed magenta)
      ctx.strokeStyle = 'rgba(80,180,255,0.6)';
      ctx.beginPath(); ctx.arc(SX(s.x), SY(s.y), 100 * scale, 0, Math.PI * 2); ctx.stroke();
      ctx.strokeStyle = 'rgba(255,60,214,0.35)';
      ctx.setLineDash([6, 6]);
      ctx.beginPath(); ctx.arc(SX(s.x), SY(s.y), 280 * scale, 0, Math.PI * 2); ctx.stroke();
      ctx.setLineDash([]);
    }
    // player foot collider (bright green circle at feet) + feet point
    const p = world.player;
    const footY = p.y + p.footOff;
    ctx.strokeStyle = '#3dff6e';
    ctx.beginPath(); ctx.arc(SX(p.x), SY(footY), Math.max(6, p.size * scale), 0, Math.PI * 2); ctx.stroke();
    ctx.fillStyle = '#3dff6e';
    ctx.beginPath(); ctx.arc(SX(p.x), SY(footY), 2.5, 0, Math.PI * 2); ctx.fill();
  }

  // status line
  ctx.font = '12px "Share Tech Mono", monospace';
  ctx.fillStyle = '#8df0a8';
  ctx.textAlign = 'left';
  const L = MAPDEV.layers;
  ctx.fillText(
    `MAPDEV  cam ${Math.round(MAPDEV.cam.x)},${Math.round(MAPDEV.cam.y)}  zoom ${MAPDEV.cam.zoom.toFixed(2)}` +
    `  [b]ounds:${MAPDEV.showChunkBounds ? 'on' : 'off'}  [o]verlays:${MAPDEV.showOverlays ? 'on' : 'off'}` +
    `  [h]orizon:${MAPDEV.showHorizon ? 'on' : 'off'}  layers 1:${+L.brightness}2:${+L.speckles}3:${+L.patches}4:${+L.paint}5:${+L.decals}6:${+L.base}`,
    10, h - 10,
  );
  ctx.restore();
}

// ─── BETA 23.06 · DT-A: PERF OVERLAY + SCREENSHOT ───────────────────────────
let _fpsEma = 60;
let _lastFrameAt = 0;
export interface PerfCounts {
  updateMs: number; renderMs: number;
  enemies: number; projectiles: number; orbs: number;
  chunks: number;
}
export function drawPerfOverlay(ctx: CanvasRenderingContext2D, w: number, counts: PerfCounts): void {
  const now = performance.now();
  if (_lastFrameAt > 0) {
    const inst = 1000 / Math.max(1, now - _lastFrameAt);
    _fpsEma += (inst - _fpsEma) * 0.08;
  }
  _lastFrameAt = now;
  ctx.save();
  ctx.font = '12px "Share Tech Mono", monospace';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'top';
  const lines = [
    `${_fpsEma.toFixed(0)} fps  upd ${counts.updateMs.toFixed(1)}ms  rnd ${counts.renderMs.toFixed(1)}ms`,
    `enemies ${counts.enemies}  proj ${counts.projectiles}  orbs ${counts.orbs}  chunks ${counts.chunks}`,
  ];
  const x = w - 12, y0 = 46;
  ctx.fillStyle = 'rgba(8,10,14,0.72)';
  const bw = 300;
  ctx.fillRect(x - bw, y0 - 4, bw + 6, lines.length * 15 + 8);
  ctx.fillStyle = _fpsEma < 50 ? '#ff6d5a' : '#8df0a8';
  lines.forEach((ln, i) => ctx.fillText(ln, x, y0 + i * 15));
  ctx.restore();
}

/** [p] — save the current canvas as a named PNG download. */
export function takeMapDevScreenshot(canvas: HTMLCanvasElement, version: string): void {
  try {
    const a = document.createElement('a');
    const zoom = MAPDEV.cam.zoom.toFixed(2);
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    a.download = `sorryboss_${version.replace(/\s+/g, '')}_zoom${zoom}_${stamp}.png`;
    a.href = canvas.toDataURL('image/png');
    a.click();
  } catch { /* dev convenience only */ }
}
